import cs from 'classnames';
import { ClaimButton } from 'components/ExhibitsExport/ClaimButton';
import { ExhibitErrorMessage } from 'components/ExhibitsExport/ExhibitErrorMessage';
import { ExhibitSelect } from 'components/ExhibitsExport/ExhibitSelect';
import { InputText } from 'components/UIComponents/InputText';
import { useResolutionExhibitTypeOptions } from 'hooks/useResolutionExhibitTypeOptions';
import { ExhibitFields } from 'pages/ExhibitExporterPage/providers';
import { useExhibitExporterContext } from 'pages/ExhibitExporterPage/providers/ExhibitExporterProvider';
import { getTemplateName } from 'pages/ExhibitExporterPage/templates/exhibitType';
import { useMemo } from 'react';
import { AddClaimPopup } from '../AddClaimPopup';
import { ClaimsTable } from '../ClaimsTable';

export const ExhibitDataSection = () => {
  const {
    value,
    claims,
    selectedClaims,
    showAddClaimPopup,
    hasEmptyClaims,
    updateValue,
    setSelectedClaims,
    deleteSelectedClaims,
    setShowAddClaimPopup,
  } = useExhibitExporterContext();

  const {
    resolutionStatusOptions,
    resolutionSubTypeOptions,
    resolutionNameOptions,
  } = useResolutionOptionsCreator(value);

  return (
    <div className="exhibit-exporter__section">
      <div className="exhibit-exporter__section-title">Exhibit Data</div>
      <div className="exhibit-exporter__section-content">
        <div className="exhibit-exporter__section-content-row">
          <InputText
            autoFocus={false}
            label="Exhibit Title*:"
            placeholder="Enter exhibit title"
            value={value.exhibitNumber}
            onChange={(e) => updateValue('exhibitNumber', e.target?.value)}
            required
            style={!value.exhibitNumber ? { border: '1px solid #e95959' } : {}}
          />
          <InputText
            autoFocus={false}
            label="Basis for objection*:"
            placeholder="Enter text"
            value={value.objectionBasis}
            onChange={(e) => updateValue('objectionBasis', e.target?.value)}
            required
            style={!value.objectionBasis ? { border: '1px solid #e95959' } : {}}
          />
        </div>

        <div className="exhibit-exporter__section-content-row">
          <ExhibitSelect
            className="exhibit-exporter__resolution-select"
            label="Resolution type:"
            placeholder="Select a resolution type"
            required
            options={resolutionStatusOptions}
            value={value?.resolutionTypeId}
            onChange={(r) => updateValue('resolutionTypeId', +r)}
          />

          <ExhibitSelect
            className="exhibit-exporter__resolution-select"
            label="Resolution subtype:"
            placeholder="Select resolution subtype"
            disabled={!value?.resolutionTypeId}
            required
            options={resolutionSubTypeOptions}
            value={value?.resolutionSubStatusId}
            onChange={(r) => updateValue('resolutionSubStatusId', +r)}
          />

          <ExhibitSelect
            className="exhibit-exporter__resolution-select"
            label="Resolution name:"
            placeholder="Select a resolution name"
            disabled={!value?.resolutionSubStatusId}
            required
            options={resolutionNameOptions}
            value={value?.resolutionActionNameId}
            onChange={(r) => updateValue('resolutionActionNameId', +r)}
          />
        </div>
      </div>
      <div
        className={cs('exhibit-exporter__section-content-row', 'align-flex-end ', {
          hide: !value?.resolutionActionNameId,
        })}
      >
        <ClaimsTable
          resolutionSubStatusId={value?.resolutionSubStatusId}
          claims={claims}
          setSelectedClaims={setSelectedClaims}
        />
        <div className="exhibit-exporter__section-content-row_buttons-right-container">
          <ClaimButton iconName="add" onClick={() => setShowAddClaimPopup(true)}>
            Add other claims
          </ClaimButton>
          <ClaimButton
            disabled={!selectedClaims.length}
            iconName="delete"
            onClick={deleteSelectedClaims}
          >
            Delete {selectedClaims?.length! > 1 ? 'claims' : 'claim'}
          </ClaimButton>
        </div>
      </div>
      {hasEmptyClaims && (
        <ExhibitErrorMessage
          text={'Please add the “Claim Number” to all the claims you want to export'}
        />
      )}
      {showAddClaimPopup && (
        <AddClaimPopup onClose={() => setShowAddClaimPopup(false)} />
      )}
    </div>
  );
};

const NONE_RESOLUTION_ID = 1;

const useResolutionOptionsCreator = (
  value:
    | Pick<ExhibitFields, 'resolutionTypeId' | 'resolutionSubStatusId'>
    | undefined,
) => {
  const { resolutionTypeId, resolutionSubStatusId } = value ?? {};

  const resolutionType = useResolutionExhibitTypeOptions();

  const resolutionStatusOptions = useMemo(() => {
    const optionsWithoutNone = resolutionType.resolutionStatusOptions.filter(
      (r) => +r.value > NONE_RESOLUTION_ID,
    );
    return optionsWithoutNone;
  }, [resolutionType.resolutionStatusOptions]);

  const resolutionSubTypeOptions = useMemo(() => {
    const subtypeOptions =
      resolutionType.resolutionSubstatusOptions(resolutionTypeId);
    const optionsWithTemplate = subtypeOptions.filter(
      (r) => !!getTemplateName(+r.value),
    );
    return optionsWithTemplate;
  }, [resolutionTypeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolutionNameOptions = useMemo(
    () =>
      resolutionType.resolutionNameOptions(resolutionTypeId, resolutionSubStatusId, {
        skipNone: true,
      }),
    [resolutionTypeId, resolutionSubStatusId], // eslint-disable-line react-hooks/exhaustive-deps
  );

  return {
    resolutionStatusOptions,
    resolutionSubTypeOptions,
    resolutionNameOptions,
  };
};
